import { useHotelWorkflow } from '../hotel/useHotelWorkflow';
import { useState } from 'react';

import {
  ClientFileUploadUrlResponse,
  useDeleteRosterRequestFileMutation,
  useGetClientFileUploadUrlMutation,
  useRosterRequestFileUrlLazyQuery,
  useUploadRosterRequestFileMutation,
  RosterRequestFile,
} from '../generated/graphql';
import { popToast } from '../utilities/popToast';

export interface UseRosterRequestFileUploadOptions {
  rosterRequestId: string;
  onUploadComplete?: () => void;
  onDeleteComplete?: () => void;
}

export function useRosterRequestFileUpload({
  rosterRequestId,
  onUploadComplete,
  onDeleteComplete,
}: UseRosterRequestFileUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const beginWorkflow = useHotelWorkflow();

  const [getFileUploadUrl] = useGetClientFileUploadUrlMutation();
  const [uploadRosterRequestFile] = useUploadRosterRequestFileMutation();
  const [deleteRosterRequestFile] = useDeleteRosterRequestFileMutation();
  const [getFileUrl] = useRosterRequestFileUrlLazyQuery();

  const uploadFile = async (
    file: File,
    displayName?: string,
  ): Promise<RosterRequestFile | null> => {
    setIsUploading(true);
    const workflow = beginWorkflow();

    try {
      // Step 1: Get signed upload URL
      const { data: urlData } = await getFileUploadUrl({
        variables: {
          input: {
            filename: file.name,
            contentType: file.type,
          },
        },
      });

      workflow.assertActive();
      const uploadUrlResponse =
        urlData?.getClientFileUploadUrl as ClientFileUploadUrlResponse;

      if (!uploadUrlResponse?.url) {
        throw new Error('Failed to get upload URL');
      }

      // Step 2: Upload file to signed URL
      const uploadResponse = await fetch(uploadUrlResponse.url, {
        method: 'PUT',
        signal: workflow.signal,
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      workflow.assertActive();
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      // Step 3: Finalize upload in backend
      const { data: uploadData } = await uploadRosterRequestFile({
        variables: {
          input: {
            rosterRequestId,
            fileType: file.type,
            tempFilePath: uploadUrlResponse.path,
            originalFilename: file.name,
            displayFilename: displayName || undefined,
          },
        },
      });

      workflow.assertActive();
      onUploadComplete?.();
      popToast('File uploaded successfully', { type: 'success' });

      return uploadData?.uploadRosterRequestFile || null;
    } catch (error) {
      if (workflow.signal.aborted) return null;
      popToast(
        `Error uploading file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
      throw error;
    } finally {
      workflow.finish();
      if (!workflow.signal.aborted) setIsUploading(false);
    }
  };

  const deleteFile = async (fileId: string): Promise<boolean> => {
    try {
      const { data } = await deleteRosterRequestFile({
        variables: { id: fileId },
      });

      if (data?.deleteRosterRequestFile) {
        onDeleteComplete?.();
        popToast('File deleted successfully', { type: 'success' });
        return true;
      }

      return false;
    } catch (error) {
      popToast(
        `Error deleting file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
      throw error;
    }
  };

  const viewFile = async (fileId: string): Promise<void> => {
    try {
      const { data } = await getFileUrl({
        variables: { id: fileId },
      });

      if (data?.rosterRequestFileUrl) {
        window.open(data.rosterRequestFileUrl, '_blank');
      } else {
        throw new Error('Could not retrieve file URL');
      }
    } catch (error) {
      popToast(
        `Error viewing file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' },
      );
    }
  };

  return {
    uploadFile,
    deleteFile,
    viewFile,
    isUploading,
  };
}
